import { useMatomo } from '@datapunt/matomo-tracker-react';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import React, { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Header from '@components/common/Header/Header';
import CopyLinkIconBtn from '@components/common/Header/components/BtnHeaderCopyLink/BtnHeaderCopyLink';
import DownloadIconBtn from '@components/common/Header/components/BtnHeaderDownload/BtnHeaderDownload';
import PrintIconBtn from '@components/common/Header/components/BtnHeaderPrint/BtnHeaderPrint';
import FilterSearch from '@components/uikit/FilterSearch/FilterSearch';

import { locationSelectAllValue } from '@constants/locationSelectAllValue';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useReportActionsMutation } from '@services/report/useReportActionsMutation';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import HardwareOverviewDetailsBlock from './components/HardwareOverviewDetailsBlock/HardwareOverviewDetailsBlock';
import {
  selectHODetailsLastViewedId,
  selectHODetailsSearchValue,
  useHODetailsActions,
} from './store/useHODetailsStore';
import style from './style.module.scss';

const HardwareOverviewDetails = () => {
  const { t } = useTranslation();
  const { trackPageView } = useMatomo();
  const navigate = useNavigate();
  const { customerId } = useParams();

  const printRef = useRef<HTMLDivElement>(null);

  const { data: profileData } = useUserProfileQuery();
  const currentDateFormat = `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`;

  const { mutate: reportActionsMutate } = useReportActionsMutation();

  const locationSelectValue = selectAppLocationSelectValue();
  const searchValue = selectHODetailsSearchValue();
  const lastViewedId = selectHODetailsLastViewedId();

  const { setHODetailsStoreSearchValue, setHODetailsLastViewedId } = useHODetailsActions();

  useEffect(() => {
    trackPageView({});
  }, []);

  useEffect(() => {
    if (locationSelectValue.id === locationSelectAllValue.id) {
      navigate(`/customer/${customerId}/hardware-overview`);
    }

    const lastViewedItemElement = document.getElementById(lastViewedId);

    if (lastViewedItemElement) {
      lastViewedItemElement.scrollIntoView();
      setHODetailsLastViewedId(null);
    }

    return () => setHODetailsStoreSearchValue('');
  }, []);

  const downloadHardwareOverviewReport = () => {
    showToast({ variant: 'success', message: t('The download will begin shortly.') });
    reportActionsMutate(
      {
        action: 'hardwareOverview',
        locations: locationSelectValue.filterIds,
      },
      {
        onSuccess: (result) => {
          saveAs(
            new Blob([result?.data], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8',
            }),
            `${profileData?.customer?.name || ''} ${t('Hardware Overview Report')} ${format(
              new Date(),
              currentDateFormat,
            )}.xlsx`,
          );
        },
      },
    );
  };

  return (
    <div className={style.hardwareOverviewDetails}>
      <Header
        title={t('Hardware Overview Details')}
        showLogo={true}
        showFacilitySelect={true}
        headerRightComponent={
          <>
            <DownloadIconBtn
              tooltipInfo={t('Download Hardware Overview Report')}
              downloadFn={downloadHardwareOverviewReport}
            />
            <CopyLinkIconBtn />
            <PrintIconBtn printRef={printRef} />
          </>
        }
        extraInfo={
          <FilterSearch
            value={searchValue}
            onChange={setHODetailsStoreSearchValue}
            variant={'header-search'}
            placeholder={t('Start to type...')}
          />
        }
        locationType={'only-facility'}
        isSecondaryPage
      />
      <div className={style.hardwareOverviewDetailsBody} ref={printRef}>
        <HardwareOverviewDetailsBlock />
      </div>
    </div>
  );
};

export default HardwareOverviewDetails;
